import _ from "lodash";
import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { connect } from "react-redux";
import loadScript from "load-script";
import axios from "axios";
import PropTypes from "prop-types";

import { SharePointOutlined } from "@toddle-edu/ds-icons";
import {
  generateRandomId,
  getFileType,
  getFileName,
  getImageDimensions,
  getMaxSize,
  getMaxSizeForZipFiles,
  getMaxSizeForVideoFiles,
  getUpdatedMaxSize,
  getFileTypesAndMaxSizes,
  getFileExtensionFromMimeType,
  getMediaCardFileType,
} from "Utils";
import {
  getFileExtensionFromFilename,
  getSharepointFileDetails,
  ARCHIVE_MIME_TYPES,
  getMimeTypeFromFileName,
} from "Utils/AttachmentHelpers";
import {
  MICROSOFT_CLIENT_ID,
  PICKER_DIRECT_DOWNLOADABLE_TYPES,
  BLOCKED_FILE_MIME_TYPES,
  BLOCKED_FILE_EXTENSIONS,
} from "Constants";

import ACLStore from "lib/aclStore";
import { Sentry } from "services/sentry";
import {
  uploadFile,
  setProgress,
  downloadFromUrl,
  removeProgressElement,
  exceededSizeError,
  heicToJpg,
  unsupportedFileExtensionError,
} from "modules/Services";

import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import DriveShareModal from "UIComponents/DriveShareModal/DriveShareModal";
import { is500MBVideoUploadLimitEnabled } from "Post/utils";

const MICROSOFT_FILE_PICKER_URL = "https://js.live.net/v7.2/OneDrive.js";

const SharepointPicker = forwardRef(
  (
    {
      parentId,
      parentType,
      sourceType,
      defaultExportType,
      attachmentExportOptions,
      multiSelect,
      enableShareModal,
      shouldUpload,
      exportAsPdf,
      maxSize,
      portalType,
      renderComponent,
      classStyle,
      setToastMsg,
      downloadFromUrl,
      onContentUploaded,
      onSingleContentUploaded,
      onMultipleFilesUploaded,
      uploadFile,
      onContentLoaded,
      onDropContent,
      removeProgressElement,
      updateFilesCount,
      exceededSizeError,
      heicToJpg,
      accept,
      excludeFileExtensionsList,
      unsupportedFileExtensionError,
      customFilterFilesHandler,
      userType,
      maxSizeForVideoFiles,
      maxSizeForZipFiles,
      isIncreasedUploadLimit,
      isCurriculumProgramFree,
    },
    ref
  ) => {
    const [showShareModal, setShowShareModal] = useState(false);
    const [files, setFiles] = useState([]);
    const [accessToken, setAccessToken] = useState("");

    useEffect(() => {
      if (!document.getElementById("microsoftFilePickerScript")) {
        loadScript(
          MICROSOFT_FILE_PICKER_URL,
          { attrs: { id: "microsoftFilePickerScript" } },
          err => {
            if (err) {
              console.error("Failed to laod script: ", err);
            }
          }
        );
      }
    }, []);

    useImperativeHandle(ref, () => {
      return {
        launchSharepointPicker() {
          const odOptions = {
            clientId: MICROSOFT_CLIENT_ID,
            action: "query",
            multiSelect: multiSelect,
            success: onSucces,
            advanced: {
              redirectUri: `${window.location.origin}/sso/microsoft`,
              isConsumerAccount: false,
            },
            cancel: onCancel,
            error: function (error) {
              console.error("Error launching SharePoint picker:", error);
            },
          };
          window.OneDrive?.open(odOptions);
        },
      };
    });

    const onSucces = data => {
      const accessToken = _.get(data, "accessToken", "");
      if (accessToken !== null && !_.isEmpty(accessToken)) {
        setAccessToken(accessToken);
        getFilesInfo({
          files: _.get(data, "value", []),
          apiEndpoint: _.get(data, "apiEndpoint"),
          accessToken: _.get(data, "accessToken", ""),
        });
      }
    };

    const onCancel = () => {};

    const getFilesInfo = async ({
      files: selectedFiles,
      apiEndpoint,
      accessToken,
    }) => {
      try {
        //creating requests to fetch selected files info
        const filesInfo = _.map(selectedFiles, selectedFile => {
          const driveId = _.get(selectedFile, "parentReference.driveId", "");
          const itemId = _.get(selectedFile, "id", "");
          return axios.get(`${apiEndpoint}drives/${driveId}/items/${itemId}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
        });

        //fetching information for selected files
        const files = _.map(
          _.filter(
            await Promise.all(filesInfo),
            responseObj => responseObj?.data
          ),
          fileResponse => {
            const fileInfo = fileResponse.data;
            return {
              downloadUrl: fileInfo["@microsoft.graph.downloadUrl"],
              url: fileInfo.webUrl,
              type: getMediaCardFileType({
                type: "FILE",
                mimeType: _.get(fileInfo, "file.mimeType", ""),
              }),
              mimeType: _.get(fileInfo, "file.mimeType", ""),
              size: fileInfo.size,
              name: fileInfo.name,
              id: fileInfo.id,
              driveId: _.get(fileInfo, "parentReference.driveId", ""),
            };
          }
        );

        //checking if files are in allowed file formats
        // Note: extension is taken from the mimeType
        let allowedFiles = _.isEmpty(accept)
          ? files
          : files.filter(file => _.includes(accept, file.mimeType));

        const notAllowedFileTypeCount = _.size(files) - _.size(allowedFiles);

        if (notAllowedFileTypeCount > 0) {
          onNotAllowedFileType({
            notAllowedFileCount: notAllowedFileTypeCount,
            fileFormats: _.filter(
              _.map(accept, allowedType => {
                let fileExtension = getFileExtensionFromMimeType({
                  mimeType: allowedType,
                });
                // as mov and qt both have same mimeType video/quicktime, we will have to explicitly handle restriction of qt files
                if (
                  fileExtension === "qt" &&
                  _.includes(excludeFileExtensionsList, ".qt")
                ) {
                  fileExtension = "mov";
                }

                if (fileExtension) return `.${fileExtension}`;
              }),
              val => !_.isEmpty(val)
            ),
          });
        }

        // filtering files on basis of excludedFileExtensionList
        // Note: extension is taken from the fileName
        const blockMaliciousMimeTypes = ACLStore.can(
          "FeatureFlag:BlockMaliciousMimeType"
        );
        const blockedFileExtensions = blockMaliciousMimeTypes
          ? _.union(excludeFileExtensionsList, BLOCKED_FILE_EXTENSIONS)
          : excludeFileExtensionsList;
        const blockedFileMimeTypes = blockMaliciousMimeTypes
          ? BLOCKED_FILE_MIME_TYPES
          : [];
        const notAllowedFileExtensions = [];
        allowedFiles = _.filter(allowedFiles, file => {
          const mimeType =
            file?.mimeType || getMimeTypeFromFileName({ fileName: file?.name });

          const extension = `.${
            getFileExtensionFromFilename({
              fileName: file?.name,
            }) || getFileExtensionFromMimeType({ mimeType })
          }`;

          if (
            _.includes(blockedFileExtensions, extension) ||
            _.includes(blockedFileMimeTypes, mimeType)
          ) {
            notAllowedFileExtensions.push(extension);
            return false;
          }
          return true;
        });

        if (_.size(notAllowedFileExtensions) > 0) {
          unsupportedFileExtensionError({
            notAllowedFileExtensions: _.uniq(notAllowedFileExtensions),
            notAllowedFileCount: _.size(notAllowedFileExtensions),
          });
        }

        // filter files based on custom logic
        if (customFilterFilesHandler) {
          allowedFiles = customFilterFilesHandler(allowedFiles);
        }

        const fileCount = _.size(allowedFiles);
        if (fileCount > 0) {
          if (enableShareModal) {
            setFiles(allowedFiles);
            setShowShareModal(true);
          } else {
            processChosenFiles({ files: allowedFiles });
          }
        }
      } catch (err) {
        console.error("Error fetching file information:", err);
      }
    };

    const onNotAllowedFileType = ({ notAllowedFileCount, fileFormats }) => {
      const fileFormatCount = _.size(fileFormats);

      const localeParams = [
        {
          key: "fileCount",
          value: notAllowedFileCount,
          isPlainText: true,
        },
        {
          key: "fileFormatCount",
          value: fileFormatCount,
          isPlainText: true,
        },
        {
          key: "entity1",
          value: `"${_.join(_.slice(fileFormats, 0, -1), ", ")}"`,
          isPlainText: true,
        },
        {
          key: "entity2",
          value: `"${_.last(fileFormats)}"`,
          isPlainText: true,
        },
      ];
      setToastMsg({
        msg:
          "toastMsgs:fileCount_file_will_be_not_uploaded_only_fileFormate_format_allowed",
        locale_params: localeParams,
        type: "warning",
        timeOut: 3500,
        position: "toast-top-center",
      });
    };

    const processChosenFiles = ({ files }) => {
      const sendObjList = [];

      //checking file size limit
      let allowedFilesWithSizeLimit = _.filter(files, file => {
        const fileType = file?.mimeType;

        const isLink = enableShareModal
          ? _.isEqual(file.exportAs, "LINK")
          : shouldExportAsLink(file.mimeType);
        if (!isLink) {
          const updatedMaxSize = getUpdatedMaxSize({
            maxSize: _.isArray(maxSize)
              ? maxSize
              : _.max(maxSize, maxSizeForVideoFiles, maxSizeForZipFiles),
            fileType,
          });

          return _.isNumber(updatedMaxSize)
            ? updatedMaxSize >= _.get(file, "size", 0)
            : true;
        }
        return true;
      });

      if (!_.isArray(maxSize)) {
        const oversizedFiles = _.filter(allowedFilesWithSizeLimit, file => {
          const isLink = enableShareModal
            ? _.isEqual(file.exportAs, "LINK")
            : shouldExportAsLink(file.mimeType);
          if (isLink) {
            return false;
          }
          let maxSizeForFile = maxSize;
          if (
            is500MBVideoUploadLimitEnabled() &&
            file?.mimeType?.startsWith("video/")
          ) {
            maxSizeForFile = maxSizeForVideoFiles;
          }

          if (
            isIncreasedUploadLimit &&
            _.includes(ARCHIVE_MIME_TYPES, file?.mimeType)
          ) {
            maxSizeForFile = maxSizeForZipFiles;
          }

          return maxSizeForFile < _.get(file, "size", 0);
        });
        if (!_.isEmpty(oversizedFiles)) {
          allowedFilesWithSizeLimit = _.filter(
            allowedFilesWithSizeLimit,
            file => {
              return !_.some(
                oversizedFiles,
                oversizedFile =>
                  file.name === oversizedFile.name &&
                  file.size === oversizedFile.size &&
                  file.lastModified === oversizedFile.lastModified
              );
            }
          );
        }
      }

      const fileCount = _.size(allowedFilesWithSizeLimit);
      _.forEach(allowedFilesWithSizeLimit, allowedFile => {
        fileDownloadAndUpload({
          file: allowedFile,
          sendObjList,
          fileCount,
        });
      });

      if (_.size(files) > _.size(allowedFilesWithSizeLimit)) {
        const fileTypesAndMaxSizes = getFileTypesAndMaxSizes({
          files,
          allowedFiles: allowedFilesWithSizeLimit,
          fileTypePropName: "mimeType",
          maxSize: maxSize,
          maxSizeForVideoFiles: maxSizeForVideoFiles,
          maxSizeForZipFiles: maxSizeForZipFiles,
          is500MBVideoLimitEnabled: is500MBVideoUploadLimitEnabled(),
          isIncreasedUploadLimit: isIncreasedUploadLimit,
        });

        exceededSizeError({
          fileTypesAndMaxSizes,
        });
      }
    };

    const fileDownloadAndUpload = async ({ file, sendObjList, fileCount }) => {
      const isLink = enableShareModal
        ? _.isEqual(file.exportAs, "LINK")
        : shouldExportAsLink(file.mimeType);
      const fileDetails = await getSharepointFileDetails({
        accessToken,
        driveId: _.get(file, "driveId", ""),
        fileId: _.get(file, "id", ""),
      });
      const thumbUrl = _.get(fileDetails, "thumbnailLink");

      const sendObj = await getSendObject(file, isLink);
      //Sending this metadata to for BE to download and upload to S3.
      //Sending this with object as thumbUrl with to show preview while creation.
      if (thumbUrl) {
        sendObj.metadata.tempThumbUrl = thumbUrl;
        sendObj.thumbUrl = thumbUrl;
      }
      updateFilesCount(1);
      const fileUploadId = generateRandomId();
      if (onDropContent) {
        //here we have either thumburl or link url(actual file url) as file url
        onDropContent({ ...sendObj, id: fileUploadId });
      }

      const startProgress = 50;
      if (!isLink) {
        //file is not of type link, i.e may be exportAsPdf is true or it is inside the allowed mmimetype
        //starting the download of file from one drive.
        downloadFromUrl({
          downloadUrl: file.downloadUrl,
          size: file.size,
          name: file.name,
          fileUploadId: fileUploadId,
          parentType: parentType,
          parentId: parentId,
          attachment: sendObj,
          onContentLoadFailed: () => {
            updateFilesCount(-1, true);
          },
          onLoaded: async fileBlob => {
            if (fileBlob) {
              fileBlob.name = _.get(file, "name", "Untitled");
              if (!fileBlob.type) {
                fileBlob.mimeType = _.get(file, "mimeType");
              }
            }
            const sendObj = onFileDownloadedFromDrive({
              file: fileBlob,
              uploadId: fileUploadId,
              startProgress: startProgress,
            });
            sendObjList.push(sendObj);

            // Sends all files sendObj together as an array
            handleOnMultipleFileUpload({ sendObjList, fileCount });
          },
        });
      } else {
        if (onContentLoaded) {
          onContentLoaded(sendObj);
        }
        // we have a link of file
        if (onContentUploaded) {
          onContentUploaded(sendObj);
        }
        if (onSingleContentUploaded) {
          onSingleContentUploaded(sendObj, fileUploadId, {
            parentId,
            parentType,
          });
        }

        sendObjList.push(sendObj);
        // Sends all files sendObj together as an array
        handleOnMultipleFileUpload({ sendObjList, fileCount });
        updateFilesCount(-1);
      }
    };

    const handleOnMultipleFileUpload = async ({ sendObjList, fileCount }) => {
      if (_.size(sendObjList) == fileCount && onMultipleFilesUploaded) {
        const values = _.filter(
          await Promise.all(sendObjList),
          urlObj => urlObj
        );
        onMultipleFilesUploaded(values);
      }
    };

    const onFileDownloadedFromDrive = async ({
      file,
      uploadId,
      startProgress,
    }) => {
      let updatedFile = file;
      if (
        ACLStore.can("FeatureFlag:EnableHEICConversion") &&
        _.includes(["image/heic", "image/heif"], _.get(file, "type"))
      ) {
        try {
          updatedFile = await heicToJpg({ file, uploadId, startProgress: 0 });
        } catch (e) {
          Sentry.captureMessage("HEIC_CONVERSION_ISSUE_", {
            extra: {
              functionName: "heicToJpg",
              reason: _.get(e, "message"),
              fileType: _.get(file, "type"),
              component: "SharepointPicker",
            },
          });
        }
      }
      if (onContentLoaded) {
        //handle your own upload
        const url = URL.createObjectURL(updatedFile);
        updatedFile["downloadUrl"] = url;

        const sendObj = await getSendObject(updatedFile);
        onContentLoaded({ ...sendObj, file: updatedFile, url });
        removeProgressElement({ id: uploadId });
        updateFilesCount(-1);
      } else {
        if (shouldUpload) {
          // uploading asset to our server. only when file has allowed mimetype

          return new Promise((resolve, reject) => {
            getSendObject(updatedFile)
              .then(sendObj => {
                const uploadStartTime = Date.now();
                const { promise } = uploadFile({
                  file: updatedFile,
                  uploadId,
                  startProgress,
                  parentType,
                  parentId,
                  attachment: sendObj,
                  portalType,
                });
                promise
                  .then(async item => {
                    let sendObj = await getSendObject({
                      ...updatedFile,
                      type: updatedFile.type,
                      size: updatedFile.size,
                      downloadUrl: item,
                    });
                    const uploadTime = Date.now() - uploadStartTime;
                    const metadata = _.get(sendObj, "metadata", {});
                    const updatedMetadata = { ...metadata, uploadTime };
                    sendObj = { ...sendObj, metadata: updatedMetadata };
                    if (onContentUploaded) {
                      await onContentUploaded(sendObj);
                    }
                    if (onSingleContentUploaded) {
                      await onSingleContentUploaded(sendObj, uploadId, {
                        parentId,
                        parentType,
                      });
                    }
                    removeProgressElement({ id: uploadId });
                    updateFilesCount(-1);
                    resolve(sendObj);
                  })
                  .catch(e => {
                    console.error(e);
                    updateFilesCount(-1, true);
                  });
              })
              .catch(reject);
          });
        }
      }
    };

    const getSendObject = async (file, isLink) => {
      const mimeType = file.mimeType ? file.mimeType : file.type;
      const url = isLink ? file.url : file.downloadUrl;
      const attachmentType = isLink ? "LINK" : getFileType(mimeType).text;
      const dimensions = await getImageDimensions({
        url,
        type: attachmentType,
      });
      const metadata = {
        size: _.get(file, "size", 0),
        serviceType: "MICROSOFT",
        fileType:
          _.get(file, "type") ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ? "document"
            : _.get(file, "type"),
        fileId: _.get(file, "id"),
        ...dimensions,
      };
      if (file.permission) {
        metadata.permission = file.permission;
      }
      return {
        type: attachmentType,
        url: url,
        name: getFileName(file.name, mimeType),
        mimeType: mimeType,
        metadata: metadata,
      };
    };

    const shouldExportAsLink = mimeType => {
      if (
        enableShareModal ||
        _.includes(PICKER_DIRECT_DOWNLOADABLE_TYPES, mimeType)
      ) {
        return false;
      }
      return !exportAsPdf;
    };

    const toggleSharingFormatModal = () => {
      setShowShareModal(prev => !prev);
    };

    return (
      <React.Fragment>
        {renderComponent ? (
          renderComponent
        ) : (
          <div className={classStyle}>
            <SharePointOutlined size="xx-small" variant="subtle" />
          </div>
        )}
        {enableShareModal && showShareModal && (
          <div
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <DriveShareModal
              serviceType={"MICROSOFT"}
              processChosenFiles={processChosenFiles}
              onClickClose={toggleSharingFormatModal}
              files={files}
              accessToken={accessToken}
              sourceType={sourceType}
              defaultExportType={defaultExportType}
              attachmentExportOptions={attachmentExportOptions}
              customStyle={{ zIndex: 1201 }}
              userType={userType}
            />
          </div>
        )}
      </React.Fragment>
    );
  }
);

SharepointPicker.displayName = "SharepointPicker";

SharepointPicker.prototype = {
  accept: PropTypes.array,
  enableShareModal: PropTypes.bool,
  onContentLoaded: PropTypes.func,
  onContentUploaded: PropTypes.func,
  updateFilesCount: PropTypes.func,
  renderComponent: PropTypes.element,
  multiSelect: PropTypes.bool,
  shouldUpload: PropTypes.bool,
  onSingleContentUploaded: PropTypes.func,
  onDropContent: PropTypes.func,
  parentId: PropTypes.string,
  parentType: PropTypes.string,
  exportAsPdf: PropTypes.bool,
  sourceType: PropTypes.oneOf(["RESOURCE", "STUDENT_TEMPLATE"]),
  attachmentExportOptions: PropTypes.array,
  customFilterFilesHandler: PropTypes.func,
};

SharepointPicker.defaultProps = {
  sourceType: "RESOURCE",
  parentType: "",
  parentId: null,
  multiSelect: true,
  exportAsPdf: false,
  enableShareModal: true,
  shouldUpload: true,
};

const mapStateToProps = (state, ownProps) => {
  let { maxSize } = ownProps;
  const { isIncreasedUploadLimit = false } = ownProps;

  const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });
  const allowInfiniteFileSize = ACLStore.can(
    "FeatureFlag:DropzoneInfiniteFileSize"
  );

  const maxSizeForVideoFiles = getMaxSizeForVideoFiles({
    maxSize,
    isCurriculumProgramFree,
    is500MBVideoLimit: is500MBVideoUploadLimitEnabled(),
    allowInfiniteFileSize,
  });

  const maxSizeForZipFiles = getMaxSizeForZipFiles({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
    isIncreasedUploadLimit,
  });

  maxSize = getMaxSize({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
  });

  return {
    maxSize,
    maxSizeForVideoFiles,
    maxSizeForZipFiles,
    userType: state.login.userInfo.user_type,
    isCurriculumProgramFree,
  };
};

const mapActionCreators = {
  uploadFile,
  setProgress,
  downloadFromUrl,
  removeProgressElement,
  setToastMsg,
  exceededSizeError,
  heicToJpg,
  unsupportedFileExtensionError,
};
export default connect(mapStateToProps, mapActionCreators, null, {
  withRef: true,
})(SharepointPicker);
